import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

// Admin-only delete button for a single conversion row. Renders nothing for
// non-admin users so affiliates only see their own earnings, never a delete
// affordance. On confirm: calls DELETE /api/admin/conversions/{id} which
// reverses the affiliate's balance credit (capped at 0) and removes the
// conversion record. The parent receives an onDeleted(conversion_id) callback
// so it can splice the row out of its local state without a full refetch.
export default function AdminDeleteConversionButton({ conversion, onDeleted }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user || user.role !== "admin") return null;

  const handleDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/conversions/${conversion.conversion_id}`);
      if (data?.balance_debited != null && data.balance_debited < (data.commission_total || 0)) {
        toast.warning(`Deleted. Partial reversal — affiliate had already been paid out ${(data.commission_total - data.balance_debited).toFixed(2)} of this commission.`);
      } else {
        toast.success("Conversion deleted, commission reversed");
      }
      setOpen(false);
      onDeleted?.(conversion.conversion_id);
    } catch (e) {
      // 404 = the conversion was already deleted (possibly by another admin
      // tab in this session). Quietly refresh the parent so the stale row
      // disappears instead of yelling at the admin.
      if (e.response?.status === 404) {
        toast.info("This conversion was already deleted");
        setOpen(false);
        onDeleted?.(conversion.conversion_id);
        return;
      }
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete this conversion");
    } finally {
      setBusy(false);
    }
  };

  const credit = Number(conversion.commission_total || 0);
  const cur = conversion.currency || "GYD";
  const credit_str = `${cur} ${credit.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Admin: delete this order"
        data-testid={`admin-delete-conversion-${conversion.conversion_id}`}
        className="text-[#9CA3AF] hover:text-[#B91C1C] p-1 rounded inline-flex items-center transition-colors"
      >
        <Trash2 size={14} />
      </button>
      <ConfirmDialog
        open={open}
        title="Delete this conversion?"
        description={
          `Order ${conversion.order_id || conversion.conversion_id} will be removed from the affiliate's history and ${credit_str} will be debited from their balance (capped at 0 so it can never go negative). If they've already been paid out for this commission, the reversal may be partial.`
        }
        confirmLabel="Yes, delete & reverse"
        busyLabel="Deleting…"
        busy={busy}
        onCancel={() => !busy && setOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}
